import { z, type ZodType } from 'zod';

import { LabelOption } from '@/services/isomorphic/ReturnsLabelService/contracts/LabelOption';
import { ReturnLabelSchema } from '@/services/isomorphic/ReturnsLabelService/contracts/schemas';
import { BasicHistoricOrderUnionSchema } from '@/services/isomorphic/OrderLookupService/data-structures/schemas';
import { isNullish } from '@/utils/null-utils';
import { ReturnReason } from '../order';
import { OrderLineSchema } from '../../Order/schemas';
import { OrderHistorySchema } from '../../OrderHistory/schemas';
import { ReturnOrderSchema } from '../order/schemas'; // Added import

import ReturnsFlowStep from './ReturnsFlowStep';
import type IReturnsFlowState from './IReturnsFlowState';
import type ICompleteReturnsFlowState from './ICompleteReturnsFlowState';
import type { IReturnOrderLine } from './IReturnOrderLine';

export const ReturnOrderLineSchema = z.object({
  id: z.string(),
  sourceOrderLine: OrderLineSchema
}) satisfies ZodType<IReturnOrderLine>;

/** Zod schema for {@link IReturnsFlowState}. */
export const ReturnsFlowStateSchema = z.object({
  currentStep: z.nativeEnum(ReturnsFlowStep),
  emailAddressOrOrderNumber: z.string().nullish(),
  billingZipCode: z.string().nullish(),
  orderHistory: OrderHistorySchema.nullish(),
  isOrderLoaded: z.boolean(),
  haveItemsToReturnBeenSelected: z.boolean(),
  isExchangeModeActive: z.boolean(),
  isReturnTypeSelectionComplete: z.boolean(),
  isReturnOrderComplete: z.boolean(),
  returnOrder: ReturnOrderSchema.partial().nullish(),
  order: BasicHistoricOrderUnionSchema.nullish(),
  orderItems: z.array(ReturnOrderLineSchema).nullish(),
  itemsSelectedForReturn: z.array(z.string()).nullish(),
  itemsPendingReturnTypeSelection: z.array(z.string()).nullish(),
  currentItemID: z.string().nullish(),
  storedReturnReason: z.nativeEnum(ReturnReason).nullish(),
  labelOption: z.nativeEnum(LabelOption).nullish(),
  returnLabel: ReturnLabelSchema.optional()
}) satisfies ZodType<IReturnsFlowState>;

/**
 * Zod schema for a Returns Flow State where the return order is complete.
 * This means all necessary information for processing the return has been gathered.
 */
export const CompletedReturnsFlowStateSchema = ReturnsFlowStateSchema.extend({
  isOrderLoaded: z.literal(true),
  isReturnTypeSelectionComplete: z.literal(true),
  isReturnOrderComplete: z.literal(true),
  // In a completed state, returnOrder must exist and be fully populated (not
  // partial).
  returnOrder: ReturnOrderSchema,
  // order and orderItems must be present if isOrderLoaded is true
  order: BasicHistoricOrderUnionSchema,
  orderItems: z.array(ReturnOrderLineSchema),
  // itemsSelectedForReturn must be present if haveItemsToReturnBeenSelected is
  // true (which it will be if isReturnOrderComplete is true)
  itemsSelectedForReturn: z.array(z.string()),
  // labelOption must be selected
  labelOption: z.nativeEnum(LabelOption)
}).refine(
  (data) => {
    if (
      !isNullish(data.returnOrder) &&
      !isNullish(data.itemsSelectedForReturn)
    ) {
      return (
        data.returnOrder.returnItems.length ===
        data.itemsSelectedForReturn.length
      );
    }

    // Fail the refinement if any of the above values is nullish.
    return false;
  },
  {
    message:
      'The number of items in returnOrder must match the number of items selected for return.'
  }
) satisfies ZodType<ICompleteReturnsFlowState>;
